/**
 * Where to send the user after they come back from the hosted login.
 *
 * This used to store `window.location.pathname` and restore it by assigning
 * to `window.location.pathname`, which silently dropped the query string at
 * both ends: a logged-out visitor following a link like
 * /plan?vendor=f5&version=16.1.2.2 landed on an empty Plan page, while a
 * logged-in visitor worked perfectly.
 *
 * Restoring by full URL means the stored value must be treated as untrusted:
 * only same-origin rooted paths are accepted, so a poisoned localStorage
 * entry cannot turn login into an open redirect.
 */
const KEY = 'redirect';

export function storeRedirect(location) {
  const loc = location || {};
  const path = `${loc.pathname || ''}${loc.search || ''}`;
  if (path) localStorage.setItem(KEY, path);
}

export function consumeRedirect() {
  const stored = localStorage.getItem(KEY);
  if (!stored) return null;
  localStorage.removeItem(KEY);

  // String-prefix checks don't model how browsers parse URLs: backslashes
  // are treated as slashes for http(s), and tabs/newlines are stripped
  // anywhere in the string before parsing. All of that can turn a value
  // that "looks like" a rooted path into a different origin. So instead of
  // matching prefixes, resolve the value with the same WHATWG URL parser
  // the browser uses and compare the *parsed* origin.
  //
  // A bare relative reference with no leading "/" (e.g. "plan?x=1") is
  // rejected outright: it isn't a scheme-qualified absolute URL, and it
  // isn't rooted either, so there's no legitimate case for it.
  let isAbsolute;
  try {
    // eslint-disable-next-line no-new
    new URL(stored);
    isAbsolute = true;
  } catch {
    isAbsolute = false;
  }
  if (!stored.startsWith('/') && !isAbsolute) return null;

  try {
    const url = new URL(stored, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    // Return the value reconstructed from the parsed URL, not the raw
    // stored string, so what gets assigned to window.location.href has
    // already been normalized by the same parser that will re-parse it.
    const rebuilt = `${url.pathname}${url.search}${url.hash}`;
    // url.pathname can itself start with "//" (e.g. stored as
    // "https://<origin>//evil.example.com/steal", or "/.//evil.example.com/
    // steal" once the "." segment is removed) — WHATWG does not collapse
    // those slashes. The origin check above passed, but a leading "//" is
    // protocol-relative once assigned to window.location.href, so the
    // browser reads it as a different authority. Reject rather than
    // sanitize: a value shaped like this is corrupt or hostile either way.
    //
    // For non-hierarchical schemes like "blob:", the origin check above is
    // no protection at all: WHATWG derives a blob URL's origin from the URL
    // it was created from (embedded after the "blob:" prefix), so
    // "blob:https://<our-origin>/<uuid>" passes the same-origin check
    // above, but `url.pathname` is then the *entire* inner URL —
    // "https://<our-origin>/<uuid>" — which doesn't start with "/" at all,
    // let alone root at this origin. Assigning that to
    // window.location.href would still land same-origin (re-parsing it
    // yields the identical origin), so this was never an open redirect, but
    // it breaks consumeRedirect's "always a rooted path" contract and, for
    // a value like "blob:https://user:pass@<our-origin>/x", would carry
    // embedded credentials into the URL bar. The contract is a rooted path,
    // so require exactly that: starts with "/", and not "//".
    if (!rebuilt.startsWith('/') || rebuilt.startsWith('//')) return null;
    return rebuilt;
  } catch {
    return null;
  }
}
